import React, { createContext, useState, useContext, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import {
  initializeScript,
  logoutAndClearData,
  getISTTime,
} from '../../utils/utils';
import { API } from '../../apis/api';
import { API_RESOURCE_URLS, USER_OPERATIONS } from '../../constants';
import { getUserProfile, buildErrorMessage } from '../../apis/calls';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';

export const AuthorizationContext = createContext(null);

const AuthorizationProvider = props => {
  const { showInternalError } = usePopupManager();
  const SSO_SESSION_CHECK_INTERVAL_MS = 60000;
  const [user, setUser] = useState({ isLoaded: false });
  const [keycloak, setKeycloak] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authPolicies, setAuthPolicies] = useState([]);
  const [authToken, setAuthToken] = useState(null);
  const generateUser = async token => {
    try {
      const { name, email, sub: userID } = jwtDecode(token);
      const userProfile = await getUserProfile();
      return { name, email, userID, ...userProfile, isLoaded: true };
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError();
    }
  };

  const loadAuthData = async token => {
    const user = await generateUser(token);
    setUser(user);
    setAuthToken(token);
    loadAuthPolicy(token);
  };

  const logoutUser = () => {
    logoutAndClearData(keycloak);
    setIsAuthenticated(false);
    setAuthToken(null);
    setUser(null);
  };

  const refreshToken = () =>
    keycloak
      .updateToken(-1) // -1 for force refresh
      .then(refreshed => {
        console.info('token refreshed:' + refreshed);
        setAuthToken(keycloak.token);
      });

  const refreshTokenAndContinueUserSessionCheckElseLogout = () =>
    refreshToken()
      .then(() => checkSSOSessionStatePeriodically())
      .catch(error => {
        console.error({ error });
        console.error(
          'Failed to refresh the token, or the session has expired.Err:' +
            buildErrorMessage(error) +
            ' Logging user out...',
        );
        console.error(buildErrorMessage(error));
        logoutUser();
      });

  const checkSSOSessionStatePeriodically = () => {
    keycloak
      .loadUserInfo()
      .then(() => {
        return setTimeout(
          checkSSOSessionStatePeriodically,
          SSO_SESSION_CHECK_INTERVAL_MS,
        );
      })
      .catch(error => {
        const {
          sub: userID,
          exp,
          iat,
          auth_time: authTime,
        } = keycloak.tokenParsed;
        console.error(
          'failed user check, error:' +
            buildErrorMessage(error) +
            `; exp:${getISTTime(exp)} iat:${getISTTime(iat)} ` +
            `auth:${getISTTime(authTime)}`,
        );
        refreshTokenAndContinueUserSessionCheckElseLogout();
      });
  };

  const loadAuthPolicy = async token => {
    try {
      const response = await API.get(API_RESOURCE_URLS.AUTH_BULK_RESOURCE, {
        params: {
          resourceGroup: 'production-planning',
          operation: Object.values(USER_OPERATIONS),
        },
      });
      setAuthPolicies(response.data);
    } catch (error) {
      console.error(buildErrorMessage(error));
    }
  };

  const setRefreshBeforeTimeout = () => {
    const expirtytimeInUnixSeconds = keycloak.tokenParsed.exp;
    const { sub: userID, exp, iat, auth_time: authTime } = keycloak.tokenParsed;
    const currentTimeInUnixSeconds = moment().unix();
    const sixtySeconds = 60;
    const remainingTimeToRefreshInMS =
      (expirtytimeInUnixSeconds - currentTimeInUnixSeconds - sixtySeconds) *
      1000;
    console.info(
      `setting token refresh - userID: ${userID} ` +
        `exp:${getISTTime(exp)} iat:${getISTTime(iat)} ` +
        `auth:${getISTTime(authTime)}`,
    );
    return setTimeout(() => {
      console.info(`userID: ${userID}: calling refresh before token expiry`);
      refreshToken().catch(err =>
        console.error('failed to refresh token ' + buildErrorMessage(err)),
      );
    }, remainingTimeToRefreshInMS);
  };

  useEffect(() => {
    initializeScript(`${window.epAppData.AUTH_ENDPOINT}/js/keycloak.js`)
      .then(() => setKeycloak(window.Keycloak('/keycloak.json')))
      .catch(err => console.log('failed to load keycloak script: err -', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authToken) return;
    const timeout = setRefreshBeforeTimeout();
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    if (!keycloak) return;
    keycloak
      .init({
        onLoad: 'login-required',
        promiseType: 'native',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then(authenticated => {
        window.keycloak = keycloak;
        setIsAuthenticated(authenticated);
        if (authenticated) {
          loadAuthData(keycloak.token);
          checkSSOSessionStatePeriodically();
        }
      })
      .catch(error =>
        console.error('keycloack init error:', buildErrorMessage(error)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak]);

  const authData = {
    user,
    isAuthenticated,
    logoutUser,
    authPolicies,
    authToken: authToken,
  };

  return <AuthorizationContext.Provider value={authData} {...props} />;
};

export const useAuthorizationContext = () => useContext(AuthorizationContext);

export default AuthorizationProvider;
